import { ancoraPorId, perfilVeiculoPorId, type AncoraOcupante } from '../didatico/perfilVeiculo'
import { perfilVitimaPorId } from '../didatico/perfisVitima'
import { colocacaoNaConfiguracao, type ConfiguracaoVeiculo } from '../didatico/configuracaoVeiculo'
import type { ColocacaoVitima } from '../didatico/rigVitima'
import { atualizarObjeto } from './operacoes'
import { pontoMundoNoVeiculo } from './referencial'
import { configuracaoDaPosicaoFinal } from './posicaoFinal'
import type { CenaTabletop, ObjetoTabletop, PosturaVitimaSolo } from './tipos'

/**
 * Âncoras do perfil de um veículo ainda livres — nenhuma outra vítima da
 * cena está vinculada a elas. Ao contrário de `ancorasLivres` (que olha uma
 * lista de `Ocupante` de um único veículo implícito), esta versão precisa
 * saber A QUAL veículo cada vítima pertence, porque a cena pode ter vários.
 */
export function ancorasLivresDoVeiculo(
  veiculo: ObjetoTabletop,
  objetos: readonly ObjetoTabletop[],
): AncoraOcupante[] {
  if (!veiculo.veiculo) return []
  const perfil = perfilVeiculoPorId(veiculo.veiculo.idPerfil)
  const ocupadas = new Set(
    objetos
      .filter((o) => o.vitima?.local.tipo === 'veiculo' && o.vitima.local.idVeiculo === veiculo.id)
      .map((o) => (o.vitima!.local as { idAncora: string }).idAncora),
  )
  return perfil.ancoras.filter((ancora) => !ocupadas.has(ancora.id))
}

/**
 * Vincula uma vítima já existente na cena a uma âncora livre de um veículo.
 *
 * Não move outras vítimas: se a âncora pedida já estiver ocupada por OUTRA
 * vítima, a operação não faz nada (o chamador decide oferecer só âncoras
 * livres, via `ancorasLivresDoVeiculo`, então isto é uma rede de segurança,
 * não o caminho esperado).
 */
export function vincularVitimaAoVeiculo(
  cena: CenaTabletop,
  idVitima: string,
  idVeiculo: string,
  idAncora: string,
): CenaTabletop {
  const vitima = cena.objetos.find((o) => o.id === idVitima)
  const veiculo = cena.objetos.find((o) => o.id === idVeiculo)
  if (!vitima?.vitima || !veiculo?.veiculo) return cena

  const jaOcupada = cena.objetos.some(
    (o) =>
      o.id !== idVitima &&
      o.vitima?.local.tipo === 'veiculo' &&
      o.vitima.local.idVeiculo === idVeiculo &&
      o.vitima.local.idAncora === idAncora,
  )
  if (jaOcupada) return cena

  return atualizarObjeto(cena, idVitima, {
    vitima: { local: { tipo: 'veiculo', idVeiculo, idAncora } },
  })
}

/**
 * Colocação da vítima NO REFERENCIAL DO VEÍCULO (não rolado, mesma convenção
 * usada por Estabilização e Extração para um único veículo) — é o que a cena
 * 3D usa para renderizar a vítima como filha do grupo do carro, herdando a
 * rolagem e a guinada dele automaticamente. `null` se a vítima não estiver
 * vinculada a um veículo, ou o veículo não existir/não ter bloco `veiculo`.
 *
 * Decúbito e sentido de cabeça ficam fixos: o Table top ainda não expõe
 * esses ajustes por vítima; só importam quando o veículo está deitado, e
 * mesmo assim só mudam a orientação fina do corpo, não se ele cabe na âncora.
 */
export function colocacaoLocalDaVitimaNoVeiculo(
  vitima: ObjetoTabletop,
  veiculo: ObjetoTabletop | undefined,
): ColocacaoVitima | null {
  const local = vitima.vitima?.local
  if (!local || local.tipo !== 'veiculo' || !veiculo?.veiculo) return null
  const perfil = perfilVeiculoPorId(veiculo.veiculo.idPerfil)
  const ancora = ancoraPorId(perfil, local.idAncora)
  const proporcoes = perfilVitimaPorId(vitima.vitima!.idPerfil)
  const configuracao: ConfiguracaoVeiculo = configuracaoDaPosicaoFinal(veiculo.veiculo.posicaoFinal)
  return colocacaoNaConfiguracao(perfil, ancora, proporcoes, configuracao, 'dorsal', 'frente')
}

/** Posição de mundo da vítima na âncora do veículo, dadas as proporções dela. */
function posicaoMundoDaVitimaNoVeiculo(vitima: ObjetoTabletop, veiculo: ObjetoTabletop) {
  const colocacao = colocacaoLocalDaVitimaNoVeiculo(vitima, veiculo)
  if (!colocacao) {
    const [x, y, z] = veiculo.transformacao.posicao
    return { x, y, z }
  }
  return pontoMundoNoVeiculo(colocacao.posicaoPelve, veiculo)
}

/**
 * Retira uma vítima do veículo e a deixa solta, na posição de mundo em que
 * ela estava (a mesma ideia de alguém encontrado ejetado, ou que o
 * instrutor solta manualmente para demonstrar transporte para o solo) — não
 * teleporta para a origem.
 *
 * Sem efeito se a vítima já estiver no solo ou não existir.
 */
export function desvincularParaOSolo(
  cena: CenaTabletop,
  idVitima: string,
  postura: PosturaVitimaSolo = 'sentada',
): CenaTabletop {
  const vitima = cena.objetos.find((o) => o.id === idVitima)
  const local = vitima?.vitima?.local
  if (!vitima?.vitima || !local) return cena

  if (local.tipo === 'solo') {
    if (local.postura === postura) return cena
    return atualizarObjeto(cena, idVitima, {
      vitima: { local: { tipo: 'solo', postura } },
    })
  }

  const veiculo = cena.objetos.find((o) => o.id === local.idVeiculo)
  if (!veiculo) return cena

  const mundo = posicaoMundoDaVitimaNoVeiculo(vitima, veiculo)
  return atualizarObjeto(cena, idVitima, {
    transformacao: { posicao: [mundo.x, mundo.y, mundo.z] },
    vitima: { local: { tipo: 'solo', postura } },
  })
}

/**
 * Quando um veículo é removido da cena, toda vítima vinculada a ele precisa
 * ir para o solo na posição de mundo em que estava — nunca sumir nem saltar
 * para a origem. Chame antes de `removerObjeto` remover o veículo.
 */
export function soltarVitimasDoVeiculoRemovido(cena: CenaTabletop, idVeiculo: string): CenaTabletop {
  const veiculo = cena.objetos.find((o) => o.id === idVeiculo)
  if (!veiculo?.veiculo) return cena

  return cena.objetos
    .filter((o) => o.vitima !== undefined && o.vitima.local.tipo === 'veiculo' && o.vitima.local.idVeiculo === idVeiculo)
    .reduce((acc, vitima) => {
      const mundo = posicaoMundoDaVitimaNoVeiculo(vitima, veiculo)
      return atualizarObjeto(acc, vitima.id, {
        transformacao: { posicao: [mundo.x, mundo.y, mundo.z] },
        vitima: { local: { tipo: 'solo', postura: 'sentada' } },
      })
    }, cena)
}

/** Uma âncora livre encontrada perto de um ponto do chão, com a distância em metros. */
export interface AncoraProximaEncontrada {
  readonly idVeiculo: string
  readonly idAncora: string
  readonly distanciaM: number
}

/**
 * Âncora livre mais próxima de um ponto do chão (mundo), dentro de um raio —
 * usada para "soltar a vítima perto do banco e ela senta ali" (arrastar e
 * soltar, ver B4). Ignora âncoras já ocupadas e veículos sem `veiculo`.
 */
export function ancoraMaisProxima(
  ponto: { readonly x: number; readonly z: number },
  objetos: readonly ObjetoTabletop[],
  raioMaximoM = 0.6,
): AncoraProximaEncontrada | null {
  let melhor: AncoraProximaEncontrada | null = null

  for (const veiculo of objetos) {
    if (!veiculo.veiculo) continue
    for (const ancora of ancorasLivresDoVeiculo(veiculo, objetos)) {
      const mundo = pontoMundoNoVeiculo(ancora.posicao, veiculo)
      const distanciaM = Math.hypot(mundo.x - ponto.x, mundo.z - ponto.z)
      if (distanciaM <= raioMaximoM && (!melhor || distanciaM < melhor.distanciaM)) {
        melhor = { idVeiculo: veiculo.id, idAncora: ancora.id, distanciaM }
      }
    }
  }

  return melhor
}

/**
 * Vincula (ou, com `null`, desvincula) a ficha clínica de atendimento de uma
 * vítima. O vínculo mora na própria cena — e não num estado solto da tela —
 * para ser salvo, exportado, importado e reiniciado junto com o cenário.
 */
export function definirFichaDaVitima(cena: CenaTabletop, idVitima: string, idFicha: string | null): CenaTabletop {
  const objeto = cena.objetos.find((o) => o.id === idVitima)
  if (!objeto?.vitima) return cena
  if ((objeto.vitima.fichaAtendimentoId ?? null) === idFicha) return cena

  const { fichaAtendimentoId: _anterior, ...semFicha } = objeto.vitima
  const vitima = idFicha ? { ...semFicha, fichaAtendimentoId: idFicha } : semFicha
  return {
    ...cena,
    objetos: cena.objetos.map((o) => (o.id === idVitima ? { ...o, vitima } : o)),
  }
}
